import { transformSearch } from "@/api/helper.ts";
import type { CaseHearing } from "@/features/case-evaluation/types/activities";
import eventService from "@/features/dashboard/api/services/event-service.ts";
import { queryClient } from "@/query-client.ts";
import { queryOptions, useMutation } from "@tanstack/react-query";
import { z } from "zod";

export const eventSearchSchema = z.object({
	search: z.string().optional(),
	include: z.string().optional(),
	calendars: z.array(z.string()).optional(),
	type: z.string().optional(),
	date: z.string().optional(),
	caseRecord: z.array(z.string()).optional(),
	caseRecordUser: z.array(z.string()).optional(),
	branch_id: z.string().optional(),
});

export const caseDownloadPrintedScheduleMutation = () => {
	return useMutation({
		mutationKey: ["case_invite_preview"],
		mutationFn: eventService.preview,
	});
};

export const eventsQueryOptions = (search?: z.infer<typeof eventSearchSchema>, options?: { enabled: boolean }) => {
	return queryOptions({
		queryKey: ["events", search],
		queryFn: () => eventService.list(transformSearch(search, ["calendars", "caseRecord", "date", "caseRecordUser", "branch_id"])),
		...options,
	});
};

export const eventsDashboardQueryOptions = (search?: z.infer<typeof eventSearchSchema>, options?: { enabled: boolean }) => {
	return queryOptions({
		queryKey: ["events_dashboard", search],
		queryFn: () => eventService.listByRange(transformSearch(search, ["calendars", "type"])),
		...options,
	});
};
export const useCaseHearingMutation = () => {
	return useMutation({
		mutationFn: (data: CaseHearing) => eventService.addHearing(data),
		meta: {
			successMessage: "Hearing saved successfully",
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["events"] });
		},
	});
};

export const useEventsMutation = () => {
	return useMutation({
		mutationFn: (data: CaseHearing) => eventService.addEvents(data),
		meta: {
			successMessage: "Events saved successfully",
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["events"] });
		},
	});
};

export const eventQueryOptions = (eventId: string, search?: z.infer<typeof eventSearchSchema>, options?: { enabled: boolean }) => {
	return queryOptions({
		queryKey: ["events", eventId, search],
		queryFn: () => eventService.findById(eventId),
		...options,
	});
};

export const useEventMutation = (eventId?: string) => {
	return useMutation({
		mutationKey: ["events", eventId],
		mutationFn: eventService.upsert,
		meta: {
			successMessage: "Event updated successfully",
		},
		onSuccess: () => queryClient.invalidateQueries({ queryKey: ["events"] }),
	});
};

export const useCaseEventsEditMutation = (eventId?: string) => {
	return useMutation({
		mutationKey: ["events", eventId],
		mutationFn: eventService.updateManualEvents,
		meta: {
			successMessage: "Event updated successfully",
		},
		onSuccess: () => queryClient.invalidateQueries({ queryKey: ["events"] }),
	});
};

export const usecaseEventsDeleteMutation = () => {
	return useMutation({
		mutationKey: ["events"],
		mutationFn: eventService.deleteManualEvents,
		meta: {
			successMessage: "Events deleted successfully",
		},
		onSuccess: () => queryClient.invalidateQueries({ queryKey: ["events"] }),
	});
};

export const useEventDeleteMutation = () => {
	return useMutation({
		mutationKey: ["events", "delete"],
		mutationFn: eventService.deleteById,
		meta: {
			successMessage: "Event archived successfully",
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["events"],
			});
		},
	});
};
